// Wiring: front screens (splash/menu/settings, screens.ts decides the flow), offline/online routing, ?perf profiling wiring for the shared render loop
// (matchView.ts). Ported from prototypes/match/index.html's inline script; split for stage 2b so the
// same frame()/hud/input/heroView code drives both a local Game and a server snapshot stream.
import * as THREE from 'three';
import { createEngraver } from './engrave.js';
import { buildMap } from './mapView.js';
import { buildNav } from '../core/layout.js';
import { CURRENT_MAP_HASH, resetActiveMap, setActiveMap } from '../core/map/runtime.js';
import type { MapData } from '../core/map/schema.js';
import { Game } from '../core/game.js';
import type { RosterEntry } from '../core/game.js';
import { HEROES, PAIRS, CLASSES, type HeroKind, type ClassId } from '../core/heroes.js';
import * as SK from '../core/skills.js';
import { mulberry32 } from '../core/rng.js';
import { createFx } from './fx.js';
import { createSound } from './sound.js';
import { renderHeroSelect } from './heroSelect.js';
import { mountCodex } from './codex.js';
import { LocalGameView } from './gameView.js';
import { runMatchView } from './matchView.js';
import { createOfflineStepper } from './offlineStep.js';
import { GAME_NAME } from './gameName.js';
import { mountFront } from './frontScreens.js';
import { readName, normalizeName } from './nameStore.js';
import { readSettings, resolveLang, resolveStyle, type Settings } from './settings.js';
import { onLangChange, setLang, t } from './i18n/index.js';
import { applyStaticI18n } from './i18n/dom.js';
import { afterSplash, backFrom, chooseMenuItem, EDITOR_HASH, FRESH_REPLAY_HASH, initialScreen, MENU_HASH, REPLAYS_HASH, replayParam, type Screen } from './screens.js';
import { checkedMap, localMaps } from './localMaps.js';
import { BAKED_CHOICE, mapChoices, resolveMapChoice, type MapChoice } from './playableMaps.js';
import type { EditorHandle } from './editor/main.js';
import { mapIssueText } from './i18n/mapIssues.js';
import { replayErrorText } from './replay/replayErrors.js';
import { isValidRoomId, type RoomId } from '../shared/protocol.js';
import { isValidReplayId } from '../shared/replay/format.js';
import { playerKey } from './net/playerKey.js';

document.title = GAME_NAME;
/* The language is settled before anything renders: the player's choice, else the browser's. A switch (only
   offered on the settings screen) re-renders that screen and the static page; every other screen and a match
   pick it up the next time they are shown. */
setLang(resolveLang(readSettings(), typeof navigator === 'undefined' ? undefined : navigator.languages));
applyStaticI18n(document);

const stageEl = document.getElementById('stage') as HTMLElement;
const legendEl = document.querySelector('.legend') as HTMLElement | null;
const frontEl = document.getElementById('front') as HTMLElement;
const heroSelectEl = document.getElementById('heroSelect') as HTMLElement;
const editorEl = document.getElementById('mapEditor') as HTMLElement;
/* Surface load/runtime errors on the page itself: not every host's console is reachable. */
(window as any).__diag = [];
const diag = (msg: string) => {
  (window as any).__diag.push(msg);
  let el = document.getElementById('diag');
  if (!el) {
    el = document.createElement('pre');
    el.id = 'diag';
    el.style.cssText = 'position:absolute;right:10px;top:60px;max-width:45%;white-space:pre-wrap;font:11px monospace;background:#fff8;color:#700;z-index:9;pointer-events:none;margin:0';
    stageEl.appendChild(el);
  }
  el.textContent = (window as any).__diag.slice(-8).join('\n');
};
window.addEventListener('error', e => diag('error: ' + e.message));
window.addEventListener('unhandledrejection', e => diag('reject: ' + (e.reason && e.reason.message || e.reason)));
window.__diagFn = diag;

const canvas = document.getElementById('view') as HTMLCanvasElement;
const hudEl = document.getElementById('hud') as HTMLElement;
const params = new URLSearchParams(location.search);

/* The WebGL side is built on first need (hero select, lobby or a direct ?hero match), not at load: the
   menus must work even where WebGL does not, and the map assets load while the player picks a hero. */
type World = { eng: ReturnType<typeof createEngraver>; mapView: ReturnType<typeof buildMap>; nav: ReturnType<typeof buildNav>; mapHash: string };
let world: World | null = null, worldFailed = false;
/** null when WebGL is unavailable — the caller shows `showWebglError()`. A failure is not retried: each
    attempt would leave another dead context behind. The map view and nav follow the active map: a switch
    since the last call (core/map/runtime.ts) rebuilds them. */
function getWorld(): World | null {
  if (world && world.mapHash !== CURRENT_MAP_HASH) {
    world.mapView.dispose();
    world.mapView = buildMap(world.eng.scene);
    world.nav = buildNav();
    world.mapHash = CURRENT_MAP_HASH;
  }
  if (world || worldFailed) return world;
  try {
    // fog and style are re-applied from settings at every match start (matchView.ts); this is only the initial compile
    const eng = createEngraver(canvas, stageEl, { fogColor: params.get('fog') !== 'mono', style: resolveStyle(readSettings(), params) });
    world = { eng, mapView: buildMap(eng.scene), nav: buildNav(), mapHash: CURRENT_MAP_HASH };
  } catch (err) {
    worldFailed = true;
    console.error(err);
    diag('webgl: ' + ((err as Error)?.message ?? err));
  }
  return world;
}
function showWebglError() {
  heroSelect?.destroy(); heroSelect = null;
  heroSelectEl.hidden = true;
  showStage(false);
  front.notice({
    title: t('main.webglTitle'),
    text: t('main.webglText'),
    onBack: () => go('menu'),
  });
}

function applyHints(s: Settings) { document.body.classList.toggle('no-hints', !s.hints); }
applyHints(readSettings());

function showStage(on: boolean) {
  stageEl.style.visibility = on ? '' : 'hidden';
  if (legendEl) legendEl.hidden = !on;
}

/* ?hero=<character> skips every screen and goes straight to a local bot match (testing/links). ?seed=<n>
   pins that match (roster pick + core bot RNG) for reproducing a run. ?online goes straight to the lobby
   (after name entry if no name is stored); see net/entry.ts for that path's own wiring. */

/* The picked character plays red with a bot from the partner class; the other pair are blue bots. Bots
   pick a random character of their class. */
function startOfflineMatch(picked: HeroKind) {
  const w = getWorld();
  if (!w) return showWebglError();
  const { eng, mapView, nav } = w;
  const { scene, camera } = eng;
  showStage(true);
  const seed = params.has('seed') ? Number(params.get('seed')) : (Date.now() >>> 0);
  const pick = mulberry32(seed);  // same core PRNG the match itself uses, seeded once for the whole run
  const any = (cls: ClassId) => CLASSES[cls].chars[Math.floor(pick() * 3)] as HeroKind;
  // «Астрал» and «Заклинатели» have no partner class: they play with a random class of a random pair.
  const mine = PAIRS.find(p => p.includes(HEROES[picked].cls)) ?? PAIRS[Math.floor(pick() * PAIRS.length)]!, theirs = PAIRS.find(p => p !== mine)!;
  const partner = mine.includes(HEROES[picked].cls) ? mine.find(c => c !== HEROES[picked].cls)! : mine[Math.floor(pick() * 2)]!;
  const roster: RosterEntry[] = [
    { kind: picked, team: 'a', player: true },
    { kind: any(partner), team: 'a' },
    { kind: any(theirs[0]), team: 'b' },
    { kind: any(theirs[1]), team: 'b' },
  ];
  const game = new Game(nav, roster, { seed });
  const view = new LocalGameView(game, roster.map(r => (r.player ? normalizeName(readName() ?? '') : null)));
  (window as any).inkbound = { game, scene, camera, THREE, SK, eng };  // console access for debugging

  const sound = createSound(camera, stageEl);
  const fx = createFx(scene, game, sound);

  const stepper = createOfflineStepper(game, view);
  runMatchView({
    scene, camera, eng, stageEl, hudEl, canvas, mapView, sound, view, params,
    stepSim: stepper.step,
    afterStep: rootOf => fx.update(rootOf),
    onBack: () => backToMenu(),
    resultsSecondary: () => ({ label: t('main.rematch'), run: stepper.rematch }),
  });
}

/* End banner's «В меню» and the lobby's: a fresh load straight to the menu. Restarting the three.js
   scene/game loop (or tearing down the online connection) in place is not worth the risk of a
   half-torn-down WebGL context. A hash-only change would not reload, hence replaceState + reload. */
function backToMenu(hash = MENU_HASH) {
  const url = new URL(location.href);
  for (const p of ['hero', 'seed', 'online', 'replay']) url.searchParams.delete(p);
  url.hash = hash;
  history.replaceState(null, '', url);
  location.reload();
}

const front = mountFront(frontEl);
let heroSelect: ReturnType<typeof renderHeroSelect> | ReturnType<typeof mountCodex> | null = null;
const hasName = () => !!readName()?.trim();
const back = (from: Screen) => { const to = backFrom(from); if (to) go(to); };
let screenNow: Screen = 'splash';

/* A bot match's map: the picker's choice, or the map the editor just sent to play (offline only). */
const EDITOR_CHOICE = 'editor';
let mapChoice = BAKED_CHOICE, editorMap: { map: MapData; label: string } | null = null;
/* Hero select opened by the editor's «Играть на этой карте» goes back to the editor, not the menu. */
let heroSelectFromEditor = false;
function botMapChoices(): MapChoice[] {
  const fromEditor: MapChoice[] = editorMap ? [{ id: EDITOR_CHOICE, label: t('main.fromEditorMap', { name: editorMap.label }), group: t('main.fromEditorGroup') }] : [];
  return [...fromEditor, ...mapChoices(localMaps())];
}
function startBotMatch(hero: HeroKind) {
  const r = mapChoice === EDITOR_CHOICE && editorMap ? checkedMap(editorMap.map) : resolveMapChoice(mapChoice, localMaps());
  if (!r.ok) {
    front.notice({ title: t('main.mapBroken'), text: r.issues.slice(0, 3).map(i => `${i.path ? `${i.path}: ` : ''}${mapIssueText(i)}`).join('; '), onBack: () => go('heroSelect') });
    return;
  }
  setActiveMap(r.value);
  if (!getWorld()) return showWebglError();
  go('match');
  startOfflineMatch(hero);
}

let mapEditor: EditorHandle | null = null;
async function openEditor() {
  front.hide();
  if (!mapEditor) {
    const [{ createMapEditor }, { chooseMapStore }] = await Promise.all([import('./editor/main.js'), import('./editor/api.js')]);
    const store = await chooseMapStore();
    if (screenNow !== 'editor' || mapEditor) return;  // left (or re-entered) while the editor loaded
    try {
      mapEditor = createMapEditor(editorEl, {
        store,
        onBack: () => back('editor'),
        onPlay: (map, label) => { editorMap = { map, label }; mapChoice = EDITOR_CHOICE; heroSelectFromEditor = true; go('heroSelect'); },
      });
    } catch (err) {
      console.error(err);
      editorEl.innerHTML = '';
      return front.notice({ title: t('main.editorFailTitle'), text: t('main.editorFailText'), onBack: () => go('menu') });
    }
  }
  if (screenNow === 'editor') mapEditor.show();
}

function go(screen: Screen) {
  screenNow = screen;
  heroSelect?.destroy(); heroSelect = null;
  heroSelectEl.hidden = true;
  mapEditor?.hide();
  showStage(false);
  switch (screen) {
    case 'splash': return front.splash({ onContinue: () => go(afterSplash(pendingReplayId !== null)) });
    case 'menu': heroSelectFromEditor = false; return front.menu({ onChoose: item => go(chooseMenuItem(item, hasName())), onBack: () => back('menu') });
    case 'settings': return front.settings({ onChange: applyHints, onBack: () => back('settings') });
    // the typed name travels on directly: re-reading the store would lose it where storage is blocked
    case 'name': return front.nameEntry({
      onDone: name => enterLobby(name),
      // Backing out of a friend's link (arrived with ?room=<id>, no stored name yet) must not leave the
      // room queued for a LATER, unrelated «Играть онлайн» from the menu — same one-shot reasoning as
      // startOnline()'s own read of this value, just reachable without ever calling it.
      onBack: () => { pendingRoomId = null; back('name'); },
    });
    case 'heroes':
      front.hide();
      heroSelectEl.hidden = false;
      heroSelect = mountCodex(heroSelectEl, { onBack: () => back('heroes') });
      return;
    case 'heroSelect': {
      if (!getWorld()) return showWebglError();  // also warms the map up while the player picks
      front.hide();
      heroSelectEl.hidden = false;
      const choices = botMapChoices();
      if (!choices.some(c => c.id === mapChoice)) mapChoice = BAKED_CHOICE;
      heroSelect = renderHeroSelect(heroSelectEl, { onBack: () => heroSelectFromEditor ? go('editor') : back('heroSelect'), onConfirm: startBotMatch, mapPicker: { choices, value: mapChoice, onChange: id => { mapChoice = id; } } });
      return;
    }
    case 'editor': return void openEditor();
    case 'lobby': return enterLobby(readName() ?? '');
    case 'match': front.hide(); return;
    case 'replays': return front.replays({
      load: async () => (await import('./replay/replayApi.js')).fetchReplayList(playerKey()),
      name: readName(),
      onOpen: id => { replayFrom = 'list'; pendingReplayId = id; go('replay'); },
      onBack: () => back('replays'),
    });
    case 'replay': return void openReplay(pendingReplayId!);
  }
}

/* The replay viewer: opened from «Мои повторы» (its «Выход» returns there) or by a ?replay=<id> link (to the
   menu). The address carries ?replay=<id> while it is open, so a reload or a copied address shows it again. */
let replayFrom: 'list' | 'link' = 'link';
function leaveReplay(started: boolean) {
  const hash = replayFrom === 'list' ? REPLAYS_HASH : MENU_HASH;
  if (started) return backToMenu(hash);  // a fresh page rather than tearing the WebGL world down in place
  pendingReplayId = null;
  const url = new URL(location.href);
  url.searchParams.delete('replay');
  history.replaceState(null, '', url.pathname + url.search);
  go(replayFrom === 'list' ? 'replays' : 'menu');
}
async function openReplay(id: string) {
  let cancelled = false;
  front.loading({ title: t('main.replayTitle'), onBack: () => { cancelled = true; leaveReplay(false); } });
  const url = new URL(location.href);
  url.searchParams.set('replay', id);
  history.replaceState(null, '', url.pathname + url.search);
  resetActiveMap();  // online matches, and so their replays, play the map baked into this build
  const [{ loadReplay }, { startReplayViewer }] = await Promise.all([import('./replay/replayApi.js'), import('./replay/replayViewer.js')]);
  const loading = loadReplay(id, { notFoundRetries: freshReplay ? 4 : 0 });
  freshReplay = false;
  // The world is built while the file downloads, not after: its own setup is the longest stall here. First
  // a moment for «Загрузка…» to paint.
  await new Promise(r => setTimeout(r, 50));
  if (cancelled || screenNow !== 'replay') return;
  if (!getWorld()) return showWebglError();
  const r = await loading;
  if (cancelled || screenNow !== 'replay') return;
  if (!r.ok) return front.notice({ title: t('main.replayFailed'), text: replayErrorText(r.error), onBack: () => leaveReplay(false) });
  const w = getWorld()!;
  front.hide();
  showStage(true);
  if (legendEl) legendEl.hidden = true;
  const { eng, mapView } = w;
  startReplayViewer({
    scene: eng.scene, camera: eng.camera, eng, stageEl, hudEl, canvas, mapView, params, sound: createSound(eng.camera, stageEl),
    file: r.file, viewerName: readName(), onExit: () => leaveReplay(true),
  });
}

function enterLobby(playerName: string) {
  front.hide();
  startOnline(playerName);
}

async function startOnline(playerName: string) {
  resetActiveMap();  // online plays the server's map, the one baked into this build
  const w = getWorld();
  if (!w) return showWebglError();
  const { eng, mapView } = w;
  const { startOnline: start } = await import('./net/entry.js');
  const roomId = pendingRoomId;
  pendingRoomId = null;  // one-shot: only the arrival that brought it here auto-joins
  start({ scene: eng.scene, camera: eng.camera, eng, stageEl, legendEl, heroSelectEl, hudEl, canvas, mapView, params, playerName, roomId, onExit: () => backToMenu() });
}

/* ?room=<id> is a friend's invite link into one specific room (see net/entry.ts's own OnlineEntryDeps
   for how it's threaded through to a `joinRoom`). Captured into `pendingRoomId` before the URL is
   stripped below — `params` itself keeps its own copy for the rest of this load (same as 'hero'/'seed'/
   'online' below), only the address bar loses it, so a later reload doesn't retry a join that has
   already happened, or already failed. */
let pendingRoomId: RoomId | null = null;
{
  const room = params.get('room');
  if (room !== null) {
    pendingRoomId = isValidRoomId(room) ? room : null;
    const url = new URL(location.href);
    url.searchParams.delete('room');
    history.replaceState(null, '', url.pathname + url.search + url.hash);
  }
}

/* ?replay=<id>: a shared replay link (see openReplay). Kept in the address; FRESH_REPLAY_HASH on it means the
   results screen just sent us here and the file may still be being written. */
let pendingReplayId: string | null = replayParam(params, isValidReplayId);
let freshReplay = location.hash === FRESH_REPLAY_HASH;

onLangChange(() => {
  applyStaticI18n(document);
  if (screenNow === 'settings') go('settings');
});

const first = initialScreen({ params, hash: location.hash, hasName: hasName(), isHero: id => id in HEROES, isRoom: isValidRoomId, isReplay: isValidReplayId });
if ([MENU_HASH, EDITOR_HASH, REPLAYS_HASH, FRESH_REPLAY_HASH].includes(location.hash)) history.replaceState(null, '', location.pathname + location.search);  // a manual reload shows the splash again
go(first);
if (first === 'match') startOfflineMatch(params.get('hero') as HeroKind);
